import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, Router, withRouterConfig } from '@angular/router';
import { ApiModule, Configuration } from '@ffd/api-client';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';

import { icons } from '@libs/icons';
import { AutoModalEscService } from '@libs/ui-lib';
import {
  AFTER_CONFIG_LOAD,
  AfterConfigLoadService,
  ConfigService,
  SharedServicesModule,
  ToastModule,
  TokenStorage,
} from '@libs/shared-services';
import { FsaListenerService } from '@libs/fsa';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import { ConfigModule } from '@schaman/angular-config';

import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import {
  appActions,
  AppEffects,
  appReducer,
  appStateKey,
  AuthEffects,
  authReducer,
  authStateKey,
  CreateAppointmentEffects,
  createAppointmentReducer,
  createAppointmentStateKey,
  PatientAlertsEffects,
  patientAlertsReducer,
  patientAlertsStateKey,
  PatientEffects,
  patientReducer,
  PatientsEffects,
  patientsReducer,
  patientsStateKey,
  patientStateKey,
} from '@libs/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideMarkdown } from 'ngx-markdown';
import { firstValueFrom } from 'rxjs';

import { CollaborationsEffects, collaborationsReducer, collaborationsStateKey } from '@libs/collaboration';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ChartingEffects, chartingReducer, chartingStateKey } from '@libs/charting';
import { ImagingEffects, imagingReducer, imagingStateKey } from '@libs/imaging';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { GoogleTagManagerConfiguration } from 'angular-google-tag-manager';
import {
  MedicalAlertDefinitionEffects,
  medicalAlertDefinitionReducer,
  medicalAlertDefinitionStateKey,
} from 'libs/store/src/lib/medical-alert-definition';
import { IOSMicEffects, iosMicReducer, iosMicStateKey } from '@libs/ios-mic';
import * as Sentry from '@sentry/angular';
import { HealthHistoryEffects, healthHistoryReducer, healthHistoryStateKey } from '../../../../libs/store/src/lib/health-history';
import { appRoutes } from './routing';
import {
  ApiHttpErrorResponseInterceptorService,
  LocalStoreTokenStorageService,
  MswLoaderService,
  PerioExamsInterceptorService,
  ProgressNotesInterceptorService,
  ScheduleInterceptorService,
  TokenInterceptorService,
  UnauthorizedInterceptorService,
} from './services';
import { PatientImageInterceptorService } from './services/imaging/patient-image-interceptor.service';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function playerFactory() {
  return player;
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'MMM d, y' } },
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(ConfigModule.forRoot({ runAsAppInitializer: true })),
    {
      provide: AFTER_CONFIG_LOAD,
      multi: true,
      deps: [MswLoaderService, ConfigService, Store, GoogleTagManagerConfiguration, FsaListenerService],
      useFactory:
        (
          mswLoaderService: MswLoaderService,
          configService: ConfigService,
          store: Store,
          googleTagManagerConfiguration: GoogleTagManagerConfiguration,
          fsaListenerService: FsaListenerService,
        ) =>
        async () => {
          if (configService.config()!.useMockApi) {
            await mswLoaderService.load(configService.config()!.apiBasePath);
          }

          if (configService.config()!.googleTagManagerId) {
            googleTagManagerConfiguration.set({
              id: configService.config()!.googleTagManagerId,
            });
          }

          store.dispatch(appActions.appInitialized());

          // Initialize FSA listener after app initialization
          fsaListenerService.connect();
        },
    },
    provideAppInitializer(() => {
      const initializerFn = ((configService: ConfigService, afterConfigLoadService: AfterConfigLoadService) => {
        return () =>
          firstValueFrom(configService.afterConfigLoad$).then(() => {
            afterConfigLoadService.runAfterConfigLoadProviders();

            return afterConfigLoadService.donePromise;
          });
      })(inject(ConfigService), inject(AfterConfigLoadService));
      return initializerFn();
    }),
    provideRouter(appRoutes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideMarkdown(),
    provideSvgIconsConfig({
      sizes: {
        xs: '12px',
        sm: '16px',
        md: '24px',
        lg: '32px',
        xl: '40px',
        xxl: '56px',
        '3xl': '64px',
        '4xl': '96px',
      },
      defaultSize: 'md',
      icons: [...icons],
    }),
    {
      provide: Configuration,
      useFactory: (configService: ConfigService): Configuration => {
        const configuration = new Configuration();
        configuration.withCredentials = true;
        configService.afterConfigLoad$.subscribe((config) => (configuration.basePath = config.apiBasePath));
        return configuration;
      },
      deps: [ConfigService],
    },
    importProvidersFrom(ApiModule),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          // ./ is needed for the electron app to find the assets folder
          useFactory: (client: HttpClient) => new TranslateHttpLoader(client, './assets/i18n/', '.json'),
          deps: [HttpClient],
        },
      }),
    ),
    importProvidersFrom(ToastModule.forRoot()),
    provideLottieOptions({
      player: playerFactory,
    }),
    TranslatePipe,
    importProvidersFrom(SharedServicesModule),
    provideStore({
      [authStateKey]: authReducer,
      router: routerReducer,
      [appStateKey]: appReducer,
      [collaborationsStateKey]: collaborationsReducer,
      [patientStateKey]: patientReducer,
      [chartingStateKey]: chartingReducer,
      [patientsStateKey]: patientsReducer,
      [imagingStateKey]: imagingReducer,
      [healthHistoryStateKey]: healthHistoryReducer,
      [medicalAlertDefinitionStateKey]: medicalAlertDefinitionReducer,
      [patientAlertsStateKey]: patientAlertsReducer,
      [createAppointmentStateKey]: createAppointmentReducer,
      [iosMicStateKey]: iosMicReducer,
    }),
    provideStoreDevtools({ maxAge: 25 }),
    provideEffects([
      AppEffects,
      AuthEffects,
      CollaborationsEffects,
      PatientEffects,
      CreateAppointmentEffects,
      PatientsEffects,
      ChartingEffects,
      ImagingEffects,
      HealthHistoryEffects,
      MedicalAlertDefinitionEffects,
      IOSMicEffects,
      PatientAlertsEffects,
    ]),
    provideRouterStore(),
    {
      provide: TokenStorage,
      useClass: LocalStoreTokenStorageService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiHttpErrorResponseInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ScheduleInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PerioExamsInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ProgressNotesInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PatientImageInterceptorService,
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    importProvidersFrom(MatLuxonDateModule),
    importProvidersFrom(BrowserAnimationsModule),
    // Initialize global ESC key support for all modals
    provideAppInitializer(() => {
      inject(AutoModalEscService);
      return Promise.resolve();
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
